import { useSelector } from 'react-redux';
import { ROLES, hasRouteAccess, hasMinimumRole, isValidRole } from '../constants/roles';

/**
 * Custom hook for role-based access control
 * @returns {Object} Role access utilities and user role information
 */
export const useRoleAccess = () => {
  const { user } = useSelector((state) => state.auth);
  
  const userRole = user?.role || null;

  /**
   * Check if user has a specific role
   * @param {string} role - Role to check
   * @returns {boolean}
   */
  const hasRole = (role) => {
    if (!userRole || !role) return false;
    return userRole.toLowerCase() === role.toLowerCase();
  };

  /**
   * Check if user has any of the specified roles
   * @param {string[]} roles - Array of roles to check
   * @returns {boolean}
   */
  const hasAnyRole = (roles) => {
    if (!userRole || !roles || !Array.isArray(roles)) return false;
    return roles.some(role => userRole.toLowerCase() === role.toLowerCase());
  };

  /**
   * Check if user has all specified roles (usually just one, but supports multiple)
   * @param {string[]} roles - Array of roles to check
   * @returns {boolean}
   */
  const hasAllRoles = (roles) => {
    if (!userRole || !roles || !Array.isArray(roles)) return false;
    return roles.every(role => userRole.toLowerCase() === role.toLowerCase());
  };

  /**
   * Check if user can access a specific route
   * @param {string} routeKey - Route key from ROUTE_PERMISSIONS
   * @returns {boolean}
   */
  const canAccessRoute = (routeKey) => {
    return hasRouteAccess(userRole, routeKey);
  };

  /**
   * Check if user has minimum role level
   * @param {string} minimumRole - Minimum required role
   * @returns {boolean}
   */
  const meetsMinimumRole = (minimumRole) => {
    return hasMinimumRole(userRole, minimumRole);
  };

  /**
   * Role checks
   */
  const isUser = hasRole(ROLES.USER);
  const isAdmin = hasRole(ROLES.ADMIN);
  const isManager = hasRole(ROLES.MANAGER);
  const isCourier = hasRole(ROLES.COURIER);
  const isNutritionist = hasRole(ROLES.NUTRITIONIST);

  /**
   * Combined role checks
   */
  const isAdminOrManager = hasAnyRole([ROLES.ADMIN, ROLES.MANAGER]);
  const isStaff = hasAnyRole([ROLES.ADMIN, ROLES.MANAGER, ROLES.COURIER, ROLES.NUTRITIONIST]);

  return {
    // User role information
    userRole,
    user,
    isValidRole: isValidRole(userRole),
    
    // Role check functions
    hasRole,
    hasAnyRole,
    hasAllRoles,
    canAccessRoute,
    meetsMinimumRole,
    
    // Specific role checks
    isUser,
    isAdmin,
    isManager,
    isCourier,
    isNutritionist,
    
    // Combined checks
    isAdminOrManager,
    isStaff,
  };
};
